import { LockClosedIcon, UserIcon } from '@heroicons/react/solid';
import { FormEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import MessageHandler from '../../components/core/MessageHandler';
import logo from '../../components/layout/logo_new.png';
import { loginUser } from '../../redux/actions/userActions';

type LoginResult =
  | null
  | { kind: 'challenge'; challengeToken: string }
  | { kind: 'cohortEnrolment'; challengeToken: string; cohort: string }
  | { kind: 'error'; message: string };

const LoginForm = () => {
  const user = useSelector((state: { user: { singleUser: unknown } }) => state.user.singleUser);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const handleFormSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email.length) {
      setError('Voer een email in');
      return;
    }
    if (!password.length) {
      setError('Voer een wachtwoord in');
      return;
    }

    // dispatch returns LoginResult — see loginUser action for the shape.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const result = (await (dispatch as any)(loginUser({ email, password, remember }))) as LoginResult;

    if (result && result.kind === 'error') {
      setError(result.message);
      return;
    }
    if (result && result.kind === 'challenge') {
      // Hand off to the MFA second-step page. The challenge token lives in
      // router state (not the URL) so it doesn't get bookmarked or leak via
      // referrer headers.
      navigate('/login/second-step', { state: { challengeToken: result.challengeToken } });
      return;
    }
    if (result && result.kind === 'cohortEnrolment') {
      // Cohort switch-on date has passed and this user has not enrolled, so
      // the server issued an enrol challenge instead of a session. Same
      // router-state handling as above.
      navigate('/login/cohort-enrolment', {
        state: {
          challengeToken: result.challengeToken,
          cohort: result.cohort,
        },
      });
      return;
    }
    navigate('/');
  };

  useEffect(() => {
    if (user) {
      navigate('/');
    }
  }, [user, navigate]);

  return (
    <div className="h-screen flex items-center justify-center">
      <form
        onSubmit={handleFormSubmit}
        className="bg-white border-grey-input_border border-2 shadow-lg rounded-2xl flex flex-col justify-around py-8 px-12 space-y-5 max-w-sm"
      >
        <img className="mx-auto p-6" src={logo} alt="School's Cool" />
        <div className="flex flex-col relative justify-around space-y-1">
          <label className="text-sm text-grey-medium">Email</label>
          <input
            type="email"
            name="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="placeholder-grey-medium text-sm rounded-lg shadow-md border-none py-2 px-3 pl-10"
            placeholder={t('auth.login_register_enter_email') ?? ''}
          />
          <UserIcon className="absolute w-5 top-6 left-2" />
          <label className="text-sm text-grey-medium pt-5">Wachtwoord</label>
          <input
            type="password"
            name="password"
            onChange={(e) => setPassword(e.target.value)}
            value={password}
            className="placeholder-grey-medium text-sm rounded-lg shadow-md border-none py-2 px-3 pl-10"
            placeholder={t('auth.login_register_enter_password') ?? ''}
          />
          <LockClosedIcon className="absolute w-5 top-24 pt-2 left-2" />
        </div>
        <div className="flex justify-between items-center">
          <label className="flex space-x-1 items-center">
            <input
              className="form-check-input appearance-none border border-gray-300 rounded-sm bg-white checked:bg-sky checked:border-sky focus:outline-none transition duration-200 mt-1 align-top bg-no-repeat bg-center bg-contain float-left mr-2 cursor-pointer"
              type="checkbox"
              onChange={(e) => setRemember(e.target.checked)}
            />
            <span className="form-check-label inline-block text-gray-800 text-sm font-medium mt-[4px]">
              Onthoud mij
            </span>
          </label>
          <Link
            to="/forgot"
            className="text-sky hover:text-sky-dark text-sm font-semibold mt-[4px]"
          >
            {t('auth.login_forgot_password')}
          </Link>
        </div>
        <button
          disabled={!email.length || !password.length}
          type="submit"
          className="rounded-lg group bg-sky text-white px-2 shadow-md py-1 hover:bg-sky-dark disabled:hover:bg-sky hover:shadow-md transition-colors"
        >
          <p>Inloggen</p>
        </button>
        <div className="flex-col space-y-1">
          <div className="text-sm text-center flex space-x-1 justify-center">
            <span>{t('auth.no_account_yet')}</span>
            <Link to="/register" className="text-sky hover:text-sky-dark">
              {t('auth.login_register')}
            </Link>
          </div>
        </div>
        <MessageHandler error={error} success="" />
      </form>
    </div>
  );
};

export default LoginForm;
